/**
 * AssurancePanel — Board-level assurance (Red Team) review.
 *
 * A formal governance gate: when enabled, an INDEPENDENT reviewer (a different
 * admin from the run owner / requester) must record findings and approve before
 * the report can be finalised or the board-facing deliverables issued. Approval
 * is invalidated if the run's data changes after sign-off.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Gavel, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_META = {
  not_requested: { cls: "text-zinc-600 bg-zinc-50 border-zinc-200", label: "No review requested" },
  requested: { cls: "text-amber-800 bg-amber-50 border-amber-200", label: "Awaiting independent sign-off" },
  approved: { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Approved" },
  rejected: { cls: "text-red-700 bg-red-50 border-red-200", label: "Rejected" },
};

export default function AssurancePanel({ runId, locked }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [findings, setFindings] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/assurance`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load assurance state");
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const setEnabled = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/assessments/runs/${runId}/assurance`, { enabled });
      setState(data);
      toast.success(enabled ? "Assurance gate enabled for this run" : "Assurance gate disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update assurance");
    } finally { setBusy(false); }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/assessments/runs/${runId}/assurance`, { action });
      setState(data);
      toast.success(action === "request" ? "Independent review requested" : "Review withdrawn");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/assurance/decision`, { decision, findings });
      setState(data);
      setFindings("");
      toast.success(decision === "approved" ? "Deliverable approved" : "Deliverable rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record decision");
    } finally { setBusy(false); }
  };

  const sm = STATUS_META[state?.status] || STATUS_META.not_requested;

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="assurance-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
          <Gavel size={14} /> Board-level assurance
        </div>
        <button type="button" onClick={load} disabled={loading}
          className="text-zinc-400 hover:text-[#7737F6] disabled:opacity-50" title="Refresh" data-testid="assurance-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <p className="text-[12px] text-zinc-500 mb-3">
        An independent reviewer (not the run owner) must sign off before this report is finalised or its
        Board Decision Paper / Pack and the Executive Decision Pack are issued. Approval lapses if the run's data changes after sign-off.
      </p>

      {/* Enable toggle */}
      <label className="flex items-center gap-2 text-[13px] text-zinc-700 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!state?.enabled}
          disabled={busy || locked}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid="assurance-enable-toggle"
        />
        Require independent assurance for this run
      </label>

      {state?.enabled && (
        <>
          <div className={`flex items-center justify-between border px-3 py-2 text-[12px] font-medium mb-3 ${sm.cls}`} data-testid="assurance-status">
            <span className="inline-flex items-center gap-1.5">
              {state.status === "approved" && !state.stale && <ShieldCheck size={14} />}
              {(state.status === "rejected" || state.stale) && <ShieldAlert size={14} />}
              {state.status === "requested" && <Clock size={14} />}
              {state.stale ? "Approval lapsed — data changed since sign-off" : sm.label}
            </span>
          </div>

          {state.reviewer && state.decided_at && (
            <div className="text-[11px] text-zinc-500 mb-2" data-testid="assurance-decision-meta">
              {state.decision === "approved" ? "Approved" : "Rejected"} by{" "}
              <strong>{state.reviewer.name || state.reviewer.email}</strong> on{" "}
              {new Date(state.decided_at).toLocaleString()}.
            </div>
          )}
          {state.findings && (
            <div className="text-[12px] text-zinc-700 bg-zinc-50 border border-zinc-200 p-2 mb-3 whitespace-pre-wrap" data-testid="assurance-findings-display">
              <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 block mb-1">Reviewer findings</span>
              {state.findings}
            </div>
          )}

          {/* Request / withdraw */}
          {(state.status === "not_requested" || state.status === "rejected" || state.stale) && !locked && (
            <button type="button" onClick={() => act("request")} disabled={busy}
              className="ek-btn ek-btn-primary text-[13px] w-full mb-2 disabled:opacity-50" data-testid="assurance-request-btn">
              Request independent review
            </button>
          )}
          {state.status === "requested" && !locked && (
            <button type="button" onClick={() => act("withdraw")} disabled={busy}
              className="text-[12px] text-zinc-500 hover:text-black mb-3" data-testid="assurance-withdraw-btn">
              Withdraw request
            </button>
          )}

          {/* Reviewer decision area */}
          {state.status === "requested" && (
            <div className="mt-3 border-t border-zinc-100 pt-3">
              {state.viewer_can_review ? (
                <>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 mb-1.5">Independent reviewer sign-off</div>
                  <textarea
                    value={findings}
                    onChange={(e) => setFindings(e.target.value)}
                    rows={3}
                    placeholder="Red-team findings — challenge the evidence, value, risk, deliverability and governance…"
                    className="w-full border border-zinc-300 px-3 py-2 text-[13px] mb-2"
                    data-testid="assurance-findings-input"
                  />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => decide("approved")} disabled={busy}
                      className="ek-btn ek-btn-primary text-[13px] inline-flex items-center gap-1 disabled:opacity-50" data-testid="assurance-approve-btn">
                      <CheckCircle2 size={14} /> Approve
                    </button>
                    <button type="button" onClick={() => decide("rejected")} disabled={busy}
                      className="ek-btn ek-btn-outline text-[13px] inline-flex items-center gap-1 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid="assurance-reject-btn">
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 p-2" data-testid="assurance-cannot-review">
                  {state.viewer_is_owner
                    ? "You are the run owner — segregation of duties means an independent colleague must sign this off."
                    : "You requested this review — a different independent reviewer must approve it."}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
